/**
 * GuestLinkError Component
 *
 * Displays a friendly error page for invalid, expired, or revoked shareable links.
 * Used on the HMCGuestPage when a share token cannot be validated.
 */

import React from 'react';

interface GuestLinkErrorProps {
  /** Error type determining the displayed message */
  errorType: 'expired' | 'revoked' | 'invalid' | 'not_found' | 'error';
  /** Optional detailed error message */
  message?: string;
}

export const GuestLinkError: React.FC<GuestLinkErrorProps> = ({ errorType, message }) => {
  const errorConfig = {
    expired: {
      icon: (
        <svg className="w-16 h-16 text-yellow-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
        </svg>
      ),
      title: 'Link Expired',
      description: 'This shareable link has expired. Please contact the person who shared it with you to request a new link.',
      bgColor: 'bg-yellow-500/10',
      borderColor: 'border-yellow-500/30',
    },
    revoked: {
      icon: (
        <svg className="w-16 h-16 text-red-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M18.364 18.364A9 9 0 005.636 5.636m12.728 12.728A9 9 0 015.636 5.636m12.728 12.728L5.636 5.636" />
        </svg>
      ),
      title: 'Link Revoked',
      description: 'This shareable link has been revoked and is no longer accessible. Please contact the person who shared it with you.',
      bgColor: 'bg-red-500/10',
      borderColor: 'border-red-500/30',
    },
    invalid: {
      icon: (
        <svg className="w-16 h-16 text-dark-muted" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M13.875 18.825A10.05 10.05 0 0112 19c-4.478 0-8.268-2.943-9.543-7a9.97 9.97 0 011.563-3.029m5.858.908a3 3 0 114.243 4.243M9.878 9.878l4.242 4.242M9.88 9.88l-3.29-3.29m7.532 7.532l3.29 3.29M3 3l3.59 3.59m0 0A9.953 9.953 0 0112 5c4.478 0 8.268 2.943 9.543 7a10.025 10.025 0 01-4.132 5.411m0 0L21 21" />
        </svg>
      ),
      title: 'Invalid Link',
      description: 'This link appears to be invalid or malformed. Please check the URL and try again.',
      bgColor: 'bg-gray-500/10',
      borderColor: 'border-gray-500/30',
    },
    not_found: {
      icon: (
        <svg className="w-16 h-16 text-dark-muted" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9.172 16.172a4 4 0 015.656 0M9 10h.01M15 10h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
        </svg>
      ),
      title: 'Link Not Found',
      description: 'We couldn\'t find this shareable link. It may have been removed or never existed.',
      bgColor: 'bg-gray-500/10',
      borderColor: 'border-gray-500/30',
    },
    error: {
      icon: (
        <svg className="w-16 h-16 text-red-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
        </svg>
      ),
      title: 'Something Went Wrong',
      description: 'We encountered an error while loading this link. Please try again later.',
      bgColor: 'bg-red-500/10',
      borderColor: 'border-red-500/30',
    },
  };

  const config = errorConfig[errorType];

  return (
    <div className="min-h-screen bg-dark-bg flex items-center justify-center p-4">
      <div className={`max-w-md w-full ${config.bgColor} ${config.borderColor} border rounded-2xl p-8 text-center`}>
        {/* Icon */}
        <div className="flex justify-center mb-6">
          {config.icon}
        </div>

        {/* Title */}
        <h1 className="text-2xl font-bold text-white mb-3 truncate">
          {config.title}
        </h1>

        {/* Description */}
        <p className="text-dark-muted mb-6">
          {message || config.description}
        </p>

        {/* Help text */}
        <div className="pt-6 border-t border-dark-border/30">
          <p className="text-sm text-dark-muted">
            Need help? Contact USI Benefits for assistance with event access.
          </p>
        </div>
      </div>
    </div>
  );
};

export default GuestLinkError;
